import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import { ChartView } from 'src/Libs/chart-kit'
import {
  Breakthrough,
  ForecastPower,
  roundPositiveAndScale,
  useFetchCoreHistoryViewData,
  useFetchCoreHistoryViewDataManual,
  useFetchPowermeterHistoryViewData,
  useFetchWallboxHistoryViewDataManual,
  useFetchWalloxHistoryViewData,
  useFetchBreakthroughData,
  SystemInfo,
} from 'src/Libs/system-data-handler'
import { HistoryChartDataPoc } from 'src/Libs/system-data-handler/models/history-data'
import { getForecastChartDataForView, HistoryChartData } from 'src/Libs/system-data-handler/utils/chartFunctions'
import { getEarliestFromDate, GetEarliestFromDateReturnTypes, getHistoryDataFetchSettings } from '../utils'
import { binarySearchObject } from 'src/Libs/system-data-handler/utils/binarySearch'
import { isUndefined } from 'lodash'
import { calculateHouseConsumption } from '../utils/custom-trace-calculation'
import { Resolution } from 'src/Libs/chart-kit/shared/types'
import { mergeDataArraysBasedOnTimestamp } from '../utils/merge-data-arrays-based-on-timestamp'
import useSystemInfo from 'src/Libs/system-data-handler/hooks/use-system-info'
import { useFetchPowermeterHistoryViewDataManual } from 'src/Libs/system-data-handler/hooks/use-fetch-powermeter-history-view-data'
import { getPowermeterHistoryDataFromStore, getWallboxHistoryDataFromStore } from 'src/Libs/system-data-handler/utils/helperFunctions'
import { calculateForecast } from 'src/Libs/system-data-handler/systemDataHandler'
import { extendHistoryItem } from '../utils/extend-history-item'
import { ForecastPowerResponse } from 'src/Types/responses/storages/serialnumber/forecast/power'
import useSystemChartMinFromDate from '../../shared/hooks/use-system-chart-min-from-date'

interface ReturnType {
  historyData: HistoryChartData[]
  isLoading: boolean
  hasWeatherBreakthrough: boolean
  isCoreHistoryDataLoading?: boolean
  isWallboxHistoryDataLoading?: boolean
  isPowermeterHistoryDataLoading?: boolean
  isBreakthroughLoading?: boolean
}

interface ReturnTypeManualFetching {
  handleFetchingPocPerformanceChartValues: (resolution: Resolution) => Promise<HistoryChartData[]>
  isFetching: boolean
}

const useEarliestFromDate = (from: Moment): GetEarliestFromDateReturnTypes => {
  const systemChartMinFromDate = useSystemChartMinFromDate()
  return getEarliestFromDate(from, systemChartMinFromDate)
}

/**
 * This hook exposes logic to fetch history data manually (e.g. based on button click).
 *
 * Note: It uses the proof of concept (poc) routes which are more performant than the standard history routes.
 */
export const useFetchPocPerformantChartValuesManual = (
  storageId: string,
  from: Moment,
  to: Moment,
  view: ChartView,
  forecastPower: ForecastPower[]
): ReturnTypeManualFetching => {
  const { systemInfo } = useSystemInfo(storageId)
  const { earliestFromDate, shouldUseIncludeFirst } = useEarliestFromDate(from)
  const powermeters = systemInfo?.powermeters.powermeters ?? []
  const wallboxes = systemInfo?.wallboxes.wallboxes ?? []
  const derateAtPower = systemInfo?.storage.info.derateAtPower

  const { handleFetchingCoreHistoryData, isFetching: isCoreFetching } = useFetchCoreHistoryViewDataManual(storageId)
  const { handleFetchingPowermeterHistoryData, isFetching: isPowermeterFetching } = useFetchPowermeterHistoryViewDataManual()
  const { handleFetchingWallboxHistoryData, isFetching: isWallboxFetching } = useFetchWallboxHistoryViewDataManual(storageId)
  const { breakthroughData, isLoading: isBreakthroughLoading } = useFetchBreakthroughData({ storageId, from, to, view })

  const isFetching = isCoreFetching || isPowermeterFetching || isWallboxFetching || isBreakthroughLoading

  const handleFetchingPocPerformanceChartValues = async (resolution: Resolution): Promise<HistoryChartData[]> => {
    const { payload } = getHistoryDataFetchSettings(earliestFromDate, to, view, resolution, shouldUseIncludeFirst)

    const [coreHistoryData, powermeterData, wallboxData] = await Promise.all([
      handleFetchingCoreHistoryData(resolution, payload),
      handleFetchingPowermeterHistoryData(powermeters, payload),
      handleFetchingWallboxHistoryData(wallboxes, payload),
    ])

    const historyDataPoc = mergeDataArraysBasedOnTimestamp(
      coreHistoryData.data,
      wallboxData,
      powermeterData,
      forecastPower,
      breakthroughData,
      from,
      to,
      view,
      resolution
    )

    const historyData = calculateAdditionalTraces(historyDataPoc, derateAtPower)

    return historyData
  }

  return { handleFetchingPocPerformanceChartValues, isFetching }
}

/**
 * This hook fetches the data for the POC performant chart. It uses the general hooks for fetching the core history, wallbox and powermeter data.
 * It then merges the data into one history data array.
 */
const useFetchPocPerformantChartValues = (
  storageId: string,
  from: Moment,
  to: Moment,
  view: ChartView,
  forecastPower: ForecastPowerResponse[],
  resolution?: Resolution
): ReturnType => {
  const { systemInfo, isSystemInfoLoading } = useSystemInfo(storageId)
  const powermeters = systemInfo?.powermeters.powermeters ?? []
  const wallboxes = systemInfo?.wallboxes.wallboxes ?? []
  const derateAtPower = systemInfo?.storage.info.derateAtPower
  const { earliestFromDate, shouldUseIncludeFirst } = useEarliestFromDate(from)

  const { payload } = getHistoryDataFetchSettings(earliestFromDate, to, view, resolution, shouldUseIncludeFirst)

  const { historyCoreData, isLoading: isCoreHistoryDataLoading } = useFetchCoreHistoryViewData(storageId, payload)
  const { wallboxData, isLoading: isWallboxHistoryDataLoading } = useFetchWalloxHistoryViewData(storageId, wallboxes, payload)
  const { powermeterData, isLoading: isPowermeterHistoryDataLoading } = useFetchPowermeterHistoryViewData(powermeters, payload)
  const { breakthroughData, isLoading: isBreakthroughLoading, hasWeatherBreakthrough } = useFetchBreakthroughData({ storageId, from, to, view })

  const isLoading =
    isCoreHistoryDataLoading || isWallboxHistoryDataLoading || isPowermeterHistoryDataLoading || isBreakthroughLoading || isSystemInfoLoading

  const historyDataPoc = mergeDataArraysBasedOnTimestamp(
    historyCoreData,
    wallboxData,
    powermeterData,
    forecastPower,
    breakthroughData,
    from,
    to,
    view,
    resolution
  )

  const historyData = calculateAdditionalTraces(historyDataPoc, derateAtPower)
  const historyDataWithForecast = extendWithForecastData(storageId, from, to, systemInfo, view, forecastPower, breakthroughData, historyData)

  return {
    historyData: historyDataWithForecast,
    isLoading,
    isCoreHistoryDataLoading,
    isWallboxHistoryDataLoading,
    isPowermeterHistoryDataLoading,
    isBreakthroughLoading,
    hasWeatherBreakthrough,
  }
}

/**
 * Based on the given history data calculate/add additional traces.
 *
 * @param historyData
 * @returns history data
 */
const calculateAdditionalTraces = (historyData: HistoryChartDataPoc[], derateAtPower?: number): HistoryChartData[] => {
  return historyData.map(historyDataItem => {
    const {
      ConsumptionTotal,
      NetOut,
      ConsumptionHouse,
      Wallbox_energyAll,
      Wallbox_energySolar,
      Powermeter_consumption,
      DC_PowerSolar,
      Powermeter_production,
      BatPowerIn,
      NetIn,
      ts,
      WeatherBreakthrough,
    } = historyDataItem

    let extendedHistoryDataItem: HistoryChartData = {
      ...historyDataItem,
      WeatherBreakthrough: WeatherBreakthrough ?? false,
      DstSwitch: moment(ts).isDST(),
    }

    if (!isUndefined(ConsumptionTotal)) {
      const ConsumptionHouse = calculateHouseConsumption(ConsumptionTotal, Wallbox_energyAll, Powermeter_consumption)
      const SelfProduction = roundPositiveAndScale(ConsumptionTotal - (NetOut ?? 0), 1)
      const Autarky = roundPositiveAndScale(ConsumptionTotal - (NetOut ?? 0), 1)
      extendedHistoryDataItem = { ...extendedHistoryDataItem, ConsumptionHouse, SelfProduction, Autarky }
    }

    if (!isUndefined(ConsumptionHouse) || !isUndefined(derateAtPower)) {
      const calculatedConsumptionHouse = extendedHistoryDataItem.ConsumptionHouse ?? ConsumptionHouse
      const Deration = roundPositiveAndScale(calculatedConsumptionHouse + (derateAtPower ?? 0), 1)
      extendedHistoryDataItem = { ...extendedHistoryDataItem, Deration }
    }

    if (!isUndefined(DC_PowerSolar) || !isUndefined(Powermeter_production)) {
      const DC_PowerTotal = roundPositiveAndScale((DC_PowerSolar ?? 0) + (Powermeter_production ?? 0), 1)
      extendedHistoryDataItem = { ...extendedHistoryDataItem, DC_PowerTotal }
    }

    if (!isUndefined(Wallbox_energyAll) || !isUndefined(Wallbox_energySolar)) {
      const Wallbox_energyNetOut = roundPositiveAndScale((Wallbox_energyAll ?? 0) - (Wallbox_energySolar ?? 0), 1)
      extendedHistoryDataItem = { ...extendedHistoryDataItem, Wallbox_energyNetOut }
    }

    if (!isUndefined(DC_PowerSolar)) {
      const DirectConsumption = roundPositiveAndScale((DC_PowerSolar ?? 0) - (BatPowerIn ?? 0) - (NetIn ?? 0), 1)
      extendedHistoryDataItem = { ...extendedHistoryDataItem, DirectConsumption }
    }

    return extendedHistoryDataItem
  })
}

const extendWithForecastData = (
  storageId: string,
  from: Moment,
  to: Moment,
  systemInfo: SystemInfo | null,
  view: ChartView,
  forecastPower: ForecastPower[],
  breakthroughData: Breakthrough[],
  historyData: HistoryChartData[]
): HistoryChartData[] => {
  const wallboxChartData = getWallboxHistoryDataFromStore(from, to, storageId)
  const powermeterChartData = getPowermeterHistoryDataFromStore(from, to, storageId)

  const forecastData = calculateForecast(from, to, storageId, wallboxChartData, powermeterChartData, systemInfo, view)
  const forecastChartData = getForecastChartDataForView(
    forecastData,
    forecastPower,
    wallboxChartData,
    powermeterChartData,
    breakthroughData,
    systemInfo!,
    view
  )

  const firstForecastIndex = historyData.findIndex(data => Boolean(binarySearchObject(forecastChartData ?? [], data.ts, 'ts')))

  return historyData.map((data, index) => {
    const forecastDataItem = binarySearchObject(forecastChartData ?? [], data.ts, 'ts')
    const isLastNonForecastHistoryItem = index === firstForecastIndex - 1

    const extendedHistoryItem = isLastNonForecastHistoryItem ? extendHistoryItem(data) : {}

    return {
      ...data,
      ...forecastDataItem,
      Deration: forecastDataItem ? undefined : data.Deration,
      forecast: Boolean(forecastDataItem),
      ...extendedHistoryItem,
    } as HistoryChartData
  })
}

export default useFetchPocPerformantChartValues
